import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { teamService } from '../../../services/teamService';

const PerformanceLeaderboard = () => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeFilter, setTimeFilter] = useState('weekly');

  useEffect(() => {
    loadLeaderboardData();
  }, [timeFilter]);

  const loadLeaderboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const endDate = new Date();
      let startDate = new Date();

      switch (timeFilter) {
        case 'daily':
          startDate?.setDate(endDate?.getDate() - 1);
          break;
        case 'weekly':
          startDate?.setDate(endDate?.getDate() - 7);
          break;
        case 'monthly':
          startDate?.setMonth(endDate?.getMonth() - 1);
          break;
        case 'quarterly':
          startDate?.setMonth(endDate?.getMonth() - 3);
          break;
        default:
          startDate?.setDate(endDate?.getDate() - 7);
      }

      const performanceData = await teamService?.getTeamPerformance({
        dateFrom: startDate?.toISOString(),
        dateTo: endDate?.toISOString()
      });

      setLeaderboardData(performanceData);

    } catch (err) {
      console.error('Error loading leaderboard data:', err);
      setError(err?.message || 'Failed to load leaderboard data');
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceBadge = (rank) => {
    switch (rank) {
      case 1:
        return { icon: 'Trophy', color: 'text-yellow-500', bg: 'bg-yellow-50' };
      case 2:
        return { icon: 'Medal', color: 'text-gray-500', bg: 'bg-gray-50' };
      case 3:
        return { icon: 'Award', color: 'text-orange-500', bg: 'bg-orange-50' };
      default:
        return { icon: 'User', color: 'text-primary', bg: 'bg-muted/30' };
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return { icon: 'TrendingUp', color: 'text-success' };
      case 'down':
        return { icon: 'TrendingDown', color: 'text-destructive' };
      default:
        return { icon: 'Minus', color: 'text-muted-foreground' };
    }
  };

  const getQualityStars = (score) => {
    const stars = [];
    const fullStars = Math.floor(score);
    const hasHalfStar = score % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars?.push(<Icon key={i} name="Star" size={12} className="text-yellow-400 fill-current" />);
    }

    if (hasHalfStar) {
      stars?.push(<Icon key="half" name="StarHalf" size={12} className="text-yellow-400 fill-current" />);
    }

    const remainingStars = 5 - Math.ceil(score);
    for (let i = 0; i < remainingStars; i++) {
      stars?.push(<Icon key={`empty-${i}`} name="Star" size={12} className="text-gray-300" />);
    }

    return stars;
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border agricultural-shadow p-6">
        <div className="flex items-center justify-center space-x-3">
          <Icon name="Loader2" size={20} className="animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Loading leaderboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border agricultural-shadow">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Performance Leaderboard</h3>
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e?.target?.value)}
            className="px-3 py-1 border border-border rounded-md text-xs bg-background"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
          </select>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={14} className="text-destructive" />
              <span className="text-destructive text-xs">{error}</span>
            </div>
          </div>
        )}
      </div>
      <div className="p-6">
        <div className="space-y-4 max-h-80 overflow-y-auto">
          {leaderboardData?.map((entry, index) => {
            const rank = index + 1;
            const badge = getPerformanceBadge(rank);
            const trend = getTrendIcon(entry?.performance?.efficiencyTrend);
            
            return (
              <div
                key={entry?.member?.id}
                className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 hover:shadow-sm ${
                  rank <= 3 ? 'border border-primary/20 bg-primary/5' : 'bg-muted/30'
                }`}
              >
                {/* Rank Badge */}
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${badge?.bg}`}>
                  {rank <= 3 ? (
                    <Icon name={badge?.icon} size={16} className={badge?.color} />
                  ) : (
                    <span className="text-xs font-bold text-muted-foreground">{rank}</span>
                  )}
                </div>
                {/* Member Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="text-sm font-medium text-foreground truncate">
                      {entry?.member?.userProfile?.fullName || 'Unknown'}
                    </h4>
                    <Icon name={trend?.icon} size={12} className={trend?.color} />
                  </div>
                  
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-xs text-muted-foreground">
                      {entry?.member?.department || entry?.member?.position || 'Team Member'}
                    </span>
                  </div>

                  {/* Quality Rating */}
                  <div className="flex items-center space-x-1">
                    {getQualityStars(entry?.performance?.qualityScore)}
                    <span className="text-xs text-muted-foreground ml-1">
                      {entry?.performance?.qualityScore?.toFixed(1)}
                    </span>
                  </div>
                </div>
                {/* Performance Stats */}
                <div className="text-right">
                  <div className="text-lg font-bold text-primary">
                    {entry?.performance?.points}
                  </div>
                  <div className="text-xs text-muted-foreground">points</div>
                  
                  <div className="flex items-center justify-end space-x-1 mt-1">
                    <span className="text-xs font-medium text-success">
                      {entry?.performance?.completionRate}%
                    </span>
                    <Icon name="CheckCircle" size={10} className="text-success" />
                  </div>
                </div>
              </div>
            );
          })}

          {leaderboardData?.length === 0 && !loading && (
            <div className="text-center py-8">
              <Icon name="Users" size={32} className="mx-auto text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground mb-2">No performance data available</p>
              <p className="text-xs text-muted-foreground">
                Team members will appear here once they complete tasks
              </p>
            </div>
          )}
        </div>

        {/* Performance Legend */}
        <div className="mt-6 pt-4 border-t border-border">
          <div className="text-xs text-muted-foreground space-y-1">
            <div className="flex items-center justify-between">
              <span>Points System:</span>
              <span>Completion + Efficiency Bonus</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Quality Score:</span>
              <span>Based on completion rate & timeliness</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceLeaderboard;